import { createSignal } from "solid-js";

const STORAGE_KEY = "scola.tenantLogo";

type StoredLogo = {
  tenantId: string;
  path: string;
  dataUrl: string;
};

const [logo, setLogo] = createSignal<StoredLogo | null>(readStored());

function readStored(): StoredLogo | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredLogo;
    if (!parsed?.dataUrl?.startsWith("data:image/")) return null;
    if (!parsed.tenantId || !parsed.path) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(value: StoredLogo | null) {
  try {
    if (!value) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // The in-memory copy still covers this browser session.
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("lecture du logo impossible"));
    reader.readAsDataURL(blob);
  });
}

/** Logo already kept for this school, or null when another school is signed in. */
export function rememberedLogo(tenantId: string | null | undefined): string | null {
  const current = logo();
  if (!current || !tenantId) return null;
  if (current.tenantId !== tenantId) return null;
  return current.dataUrl;
}

export async function rememberTenantLogo(file: Blob, storagePath: string): Promise<void> {
  const tenantId = storagePath.split("/")[0];
  if (!tenantId) return;
  const dataUrl = await blobToDataUrl(file);
  const next = { tenantId, path: storagePath, dataUrl };
  writeStored(next);
  setLogo(next);
}

/** Load the saved file into memory when this browser does not have it yet. */
export async function hydrateTenantLogo(
  tenantId: string,
  load: () => Promise<Blob | null>,
): Promise<void> {
  if (rememberedLogo(tenantId)) return;
  const blob = await load();
  if (!blob || blob.size === 0) return;
  await rememberTenantLogo(blob, `${tenantId}/branding/logo`);
}
